import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { asUserId, type UserId } from '@leanin/shared/ids';
import { useAuthedSession } from '@/lib/use-session';
import {
  mapProfileSelfToView,
  memberSinceFromCreatedAt,
} from '@/components/profile/mapper';
import {
  ProfileChrome,
  type ProfileHeaderData,
} from '@/components/profile/ProfileChrome';
import { ProfileChromeSkeleton } from '@/components/profile/ProfileChromeSkeleton';
import { ProfileContentView } from '@/components/profile/ProfileContentView';
import { ErrorState, useDelayedLoading } from '@leanin/ui';
import { RetryButton } from '@/components/states/index';
import {
  fetchMyProfile,
  identityKeys,
} from '@/lib/queries/identity';
import {
  fetchMyCircles,
  fetchMyNetworks,
} from '@/lib/queries/groups';
import { feedKeys, fetchPostsByAuthor } from '@/lib/queries/feed';
import { authoredPostToFeedPost } from '@/components/feed/feed-item-to-post';
import { formatRelativeShort } from '@/lib/format-date';
import type { Profile } from '@/components/profile/types';

export const Route = createFileRoute('/_authed/profile/')({
  component: ProfilePage,
});

function buildHeadline(data: {
  jobTitle?: string | null;
  company?: string | null;
  bio?: string | null;
}): string {
  const role = data.jobTitle ?? '';
  const company = data.company ?? '';
  if (role && company) return `${role} at ${company}`;
  return role || company || data.bio?.slice(0, 80) || '';
}

function ProfilePage() {
  // Use the cached session member ID so all queries fire in parallel rather
  // than waiting for meQuery to resolve before enabling the collateral fetches.
  const session = useAuthedSession();
  const userId: UserId | null = session ? asUserId(session.userId) : null;

  const meQuery = useQuery({
    queryKey: identityKeys.me(),
    queryFn: fetchMyProfile,
  });

  const me = meQuery.data;

  const circlesQuery = useQuery({
    queryKey: ['profile', 'self', 'circles'] as const,
    queryFn: () => fetchMyCircles({ pageSize: 6 }),
    enabled: userId !== null,
  });
  const networksQuery = useQuery({
    queryKey: ['profile', 'self', 'networks'] as const,
    queryFn: () => fetchMyNetworks(),
    enabled: userId !== null,
  });
  // Profile post list pagination. Server `limit` caps at 50 (see
  // posts/routes/list-posts-by-author.ts). Start with 5; the "Load
  // more" button adds 5 each click.
  const POSTS_PAGE = 5;
  const POSTS_MAX = 50;
  const [postsLimit, setPostsLimit] = useState<number>(POSTS_PAGE);
  const postsQuery = useQuery({
    queryKey: userId
      ? feedKeys.authorPosts(userId, postsLimit)
      : ['posts', 'unknown'],
    queryFn: () => fetchPostsByAuthor(userId as string, postsLimit),
    enabled: userId !== null,
    // Bumping `postsLimit` rotates the query key. Without
    // `keepPreviousData` React Query treats the new key as a fresh
    // query with no data, which flips `postsQuery.isLoading` true and
    // collapses the whole page to its skeleton until the new page
    // lands — the visible "spazz" reported. With the placeholder, the
    // previous page of posts stays painted while the next page fetches.
    placeholderData: keepPreviousData,
  });

  const showSkeleton = useDelayedLoading(meQuery.isLoading || !me);
  if (showSkeleton) return <ProfileChromeSkeleton />;
  if (meQuery.isLoading || !me) return null;
  if (meQuery.isError) {
    return (
      <ErrorState
        title="Couldn't load your profile"
        body="Something on our end didn't respond. Give it a moment and try again."
        primary={<RetryButton />}
      />
    );
  }

  // `/profile` is the SELF-VIEW preview: it renders the public-facing
  // profile body as another member would see it, so the leader can verify
  // how their profile reads before editing. The chrome keeps `isViewer:
  // true` so the "Edit profile" CTA appears on the header; the body itself
  // is always read-only — editing happens at /settings/account.
  const header: ProfileHeaderData = {
    id: me.id,
    name:
      [me.firstName, me.lastName].filter(Boolean).join(' ').trim() || 'Member',
    headline: buildHeadline(me),
    location: me.location ?? '',
    memberSince: memberSinceFromCreatedAt(me.createdAt),
    photo: me.photoUrl ?? undefined,
    coverPhotoUrl: me.coverPhotoUrl ?? undefined,
    isViewer: true,
    isFollowing: false,
  };

  const collateralReady =
    !circlesQuery.isLoading &&
    !networksQuery.isLoading &&
    !postsQuery.isLoading;

  let profile: Profile | null = null;
  if (collateralReady) {
    const circles = circlesQuery.data?.items ?? [];
    const networks = networksQuery.data?.items ?? [];
    const posts = postsQuery.data ?? [];

    profile = mapProfileSelfToView(me, {
      circles: circles.map((c) => ({
        id: c.id,
        name: c.name,
        role: c.role === 'leader' ? 'Leader' : 'Member',
        meta: `${c.memberCount} member${c.memberCount === 1 ? '' : 's'}`,
        imageUrl: c.imageUrl,
      })),
      networks: networks.map((n) => ({
        id: n.id,
        name: n.name,
        memberCount: n.memberCount,
        imageUrl: n.imageUrl,
      })),
      posts: posts.map((p) => authoredPostToFeedPost(p, formatRelativeShort)),
    });
  }

  const postsFetched = postsQuery.data?.length ?? 0;
  const hasMorePosts = postsFetched >= postsLimit && postsLimit < POSTS_MAX;

  return (
    <ProfileChrome header={header}>
      <ProfileContentView
        profile={profile}
        hasMorePosts={hasMorePosts}
        loadingMorePosts={postsQuery.isFetching}
        onLoadMorePosts={() =>
          setPostsLimit((n) => Math.min(n + POSTS_PAGE, POSTS_MAX))
        }
      />
    </ProfileChrome>
  );
}
